import type { ZodType } from 'zod'
import type { AnchorPosition } from './agent-command-anchor.js'

/**
 * Declarative description of an agent-callable editor command.
 *
 * Lives in `@tunasong/schemas` (not `@tunasong/plugin-lib`) so backend
 * runtimes (e.g., the ai-api Lambda) can validate and serialize the
 * command surface without pulling Plate/Slate/React. plugin-lib
 * re-exports these symbols for plugin authors.
 */
export interface AgentCommandSpec<TArgs = unknown> {
  /** e.g., 'chord.insert', 'section.add' */
  commandId: string
  /** One-line description used in the system-prompt manifest. */
  description: string
  /** Zod schema for args. Validation + JSON-schema export. */
  argsSchema: ZodType<TArgs>
  /** Entity types where the command is available, e.g. ['song', 'page']. */
  entityTypes: string[]
  /** Anchor positions the command accepts. Validated server-side before proposal creation. */
  anchorPositions: AnchorPosition[]
  /**
   * When true, the proposal UI shows the verb in destructive color and
   * focuses Reject by default. Single bit, no tier system.
   */
  isDestructive?: boolean
  /**
   * When false, firings of a rule using this command cannot be fully undone.
   * Advisory — the authoritative per-firing signal is the handler-returned
   * inverse (Decision 7). The rule-builder warns at compose time; undo skips
   * irreversible steps. Omitted = unknown/not-applicable (treated as no warning),
   * which is why this has no default unlike `isDestructive`.
   */
  isReversible?: boolean
}

const registry = new Map<string, AgentCommandSpec>()

export const registerAgentCommand = <TArgs>(spec: AgentCommandSpec<TArgs>): void => {
  if (registry.has(spec.commandId)) {
    throw new Error(`agent-command-registry: '${spec.commandId}' is already registered`)
  }
  registry.set(spec.commandId, { isDestructive: false, ...spec } as AgentCommandSpec)
}

export const getCommandSpec = (commandId: string): AgentCommandSpec | undefined => registry.get(commandId)

export const getCommandsForEntityType = (entityType: string): AgentCommandSpec[] =>
  Array.from(registry.values()).filter(spec => spec.entityTypes.includes(entityType))

/**
 * Test helper. Exported from the barrel for cross-package tests; the
 * explicit name signals "do not call from production code".
 * @internal
 */
export const resetRegistryForTests = (): void => {
  registry.clear()
}
